import CustomForm from "@components/form/CustomForm";
import PageWrapper from "@components/layouts/PageWrapper";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { removeKeys } from "@utils/general/removeKeys";
import { Toast } from "@helpers/popups/Toast";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import {
  TWarehouse,
  warehouseSchema,
} from "@helpers/validation-schemas/warehouse-and-inventory/warehouse.schema";
import { useUpdateWarehouseMutation } from "@states/warehouse-and-inventory/warehouse.api";
import { warehouseFields } from "@helpers/fields/warehouse-and-inventory/warehouse.fields";
import {
  useGetAllMainWarehouseListQuery,
  useGetAllStatesQuery,
} from "@states/common/common.api";
import {
  addCities,
  addStates,
  addTownships,
} from "@states/common/common.slice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@states/store";
import { WAREHOUSE } from "@utils/constants/warehouse.constants";
import BackButton from "@components/buttons/BackButton";
import AuthComponent from "@components/auth/component";
import { ALLOCATION_ACCOUNT } from "@utils/constants/allocation-account.constants";
import { allocationAccountSchema } from "@helpers/validation-schemas/warehouse-and-inventory/allocation-account.schema";
import { allocationAccountColumns } from "@helpers/columns/warehouse-and-inventory/allocation-account.columns";
import { allocationAccountFields } from "@helpers/fields/allocation-and-inventory/allocation-account.fields";

const AllocationAccountEditPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [cities, setCities] = useState();
  const [townships, setTownships] = useState();
  const dispatch = useDispatch<AppDispatch>();
  const [selectedType, setSelectedType] = useState(2);

  const {
    states: storedStates,
    cities: storedCities,
    townships: storedTownships,
  } = useSelector((state: RootState) => state.common);

  const { warehouse } = location.state as {
    warehouse: TWarehouse;
  };
  const { data: warehouseData } = useGetAllMainWarehouseListQuery();
  useEffect(() => {
    if (warehouse?.warehouse_type) {
      // let type = ;
      //@ts-ignore
      setSelectedType(warehouse?.warehouse_type);
    }
  }, [warehouse, setSelectedType]);
  console.log("selectedType aaa====>", selectedType);
  const { data: stateData } = useGetAllStatesQuery();

  const [updateWarehouse, { isLoading, error: updateError, isSuccess }] =
    useUpdateWarehouseMutation();

  useErrorHandling(...castTypeArr<TFailResponse>([updateError]));

  const fields = useMemo(() => {
    return allocationAccountFields({
      data: {
        states: stateData || [],
        cities: cities || [],
        townships: townships || [],
        warehouseData: warehouseData || [],
      },
      selectActions: {
        stateAction: addStates,
        cityAction: addCities,
        townshipAction: addTownships,
      },
      disabled: {
        cityDisabled: storedStates.length <= 0,
        townshipDisabled: storedCities.length <= 0,
        warehouseDisabled: true,
      },
      selectSingleIds: {
        stateId:
          storedStates[0]?.warehouse_division_id ||
          warehouse.warehouse_division_id,
        cityId:
          storedCities[0]?.warehouse_city_id || warehouse.warehouse_city_id,
        townshipId:
          storedTownships[0]?.warehouse_township_id ||
          warehouse.warehouse_township_id,
      },
      values: {
        country: 1,
        selectedType,
        warehouseValue: warehouse.main_warehouse_name,
        state:
          warehouse.warehouse_division_name || warehouse.warehouse_state_name,
        city: warehouse.warehouse_city_name,
        township: warehouse.warehouse_township_name,
      },
    });
  }, [
    stateData,
    warehouseData,
    cities,
    townships,
    storedStates,
    storedCities,
    storedTownships,
    warehouse,
    selectedType,
  ]);

  useEffect(() => {
    if (warehouse) {
      const {
        warehouse_division_id,
        warehouse_state_name,
        warehouse_division_name,
        warehouse_city_id,
        warehouse_city_name,
      } = warehouse;
      const stateToAdd = {
        warehouse_division_id,
        name: warehouse_division_name || warehouse_state_name,
      };
      const cityToAdd = { warehouse_city_id, name: warehouse_city_name };
      dispatch(addStates([stateToAdd]));
      dispatch(addCities([cityToAdd]));
    }
    return () => {
      dispatch(addStates([]));
      dispatch(addCities([]));
      dispatch(addTownships([]));
    };
  }, []);

  useEffect(() => {
    if (storedStates.length > 0 && stateData) {
      const state = storedStates[0];
      const foundState: any = stateData.find(
        (sd: any) => sd.stateid === state.warehouse_division_id
      );

      if (foundState) {
        setCities(foundState.cities);
      }
    }
  }, [storedStates, stateData]);

  useEffect(() => {
    if (storedCities.length > 0 && storedStates.length > 0 && stateData) {
      const state = storedStates[0];
      const city = storedCities[0];

      const foundState: any = stateData.find(
        (sd: any) => sd.stateid === state?.warehouse_division_id
      );

      if (foundState) {
        const foundCity = foundState.cities.find(
          (c: any) => c.cityid === city.warehouse_city_id
        );

        if (foundCity) {
          setTownships(foundCity.townships);
        }
      }
    }
  }, [storedCities, storedStates, stateData]);

  const asyncDispatcher = useCallback(
    async (reqBody: any) => {
      try {
        const rest = removeKeys(
          reqBody,
          "warehouse_division_name",
          "warehouse_city_name",
          "warehouse_township_name"
        );
        // const rest = removeKeys(reqBody, 'remark', 'description');
        const res = await updateWarehouse({
          id,
          
          body: { warehouse_type: 2,...rest },
        });

        const successData = "data" in res ? res.data : null;
        const { success, message } = successData;

        if (success || isSuccess) {
          Toast.fire({
            title: message,
            icon: "success",
          });

          navigate(ALLOCATION_ACCOUNT.BASE_PATH, {
            state: {
              reload: true,
            },
          });
        }
      } catch (error) {
        console.log("Error", error);
      }
    },
    [updateWarehouse, isSuccess, Toast, navigate, selectedType]
  );

  return (
    <AuthComponent
      action="edit"
      module="Stock & Inventory"
      subModule="Allocation Account"
      page
    >
      <PageWrapper>
        {isLoading && <LoadingSpinner />}
        <BackButton />
        <h3 className="mb-4 ml-2 text-2xl font-[600] text-primary-dark uppercase">
          {ALLOCATION_ACCOUNT.EDIT_PAGE_TITLE}
        </h3>
        <CustomForm
          schema={allocationAccountSchema}
          fields={fields}
          backbtn={false}
          defaultValues={warehouse}
          asyncDispatcher={asyncDispatcher}
        />
      </PageWrapper>
    </AuthComponent>
  );
};

export default AllocationAccountEditPage;
